import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

export const ChatHeaderRoot = styled(Box, { label: 'ChatHeaderRoot' })(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  height: 64,
  minHeight: 64,
  boxSizing: 'border-box',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.common.white,
}));

export const ChatHeaderLeft = styled(Box, { label: 'ChatHeaderLeft' })(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  // Stretch to the full header height so alignItems:center centers the avatar
  // against the whole header, not just the (taller, 2-row) title area.
  alignSelf: 'stretch',
  gap: theme.spacing(1),
  flex: 1,
  overflow: 'hidden',
}));

export const ChatHeaderTitleArea = styled(Box, { label: 'ChatHeaderTitleArea' })(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  overflow: 'hidden',
  // minWidth: 0 is required for text-overflow: ellipsis to work inside a flex child
  minWidth: 0,
  gap: theme.spacing(0.25),
}));

export const ChatHeaderNameRow = styled(Box, { label: 'ChatHeaderNameRow' })(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
  overflow: 'hidden',
}));

export const ChatHeaderChannelName = styled(Typography, { label: 'ChatHeaderChannelName' })(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  fontSize: theme.typography.pxToRem(18),
  lineHeight: 1.25,
  color: theme.palette.text.primary,
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})) as typeof Typography;

export const ChatHeaderCompanyName = styled(Typography, { label: 'ChatHeaderCompanyName' })(({ theme }) => ({
  color: theme.palette.text.secondary,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})) as typeof Typography;

export const ChatHeaderActionButton = styled(IconButton, { label: 'ChatHeaderActionButton' })(({ theme }) => ({
  width: 32,
  height: 32,
  padding: theme.spacing(0.5),
  color: theme.palette.action.active,
  '& .MuiSvgIcon-root': {
    fontSize: theme.typography.pxToRem(24),
  },
})) as typeof IconButton;

export const ChatHeaderRight = styled(Box, { label: 'ChatHeaderRight' })(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexShrink: 0,
  gap: theme.spacing(2),
}));

export const AvatarWrapper = styled(Box, { label: 'AvatarWrapper' })({
  flexShrink: 0,
  // Flex-center so the 32px avatar sits centered rather than top-aligned within
  // the wrapper's line-height-driven height.
  display: 'flex',
  alignItems: 'center',
});

export const EmptyPanelHeader = styled(Box, { label: 'EmptyPanelHeader' })(({ theme }) => ({
  height: 64,
  minHeight: 64,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  paddingRight: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

// White circular badge wrapping the gradient chat icon (Figma node 21886-26024):
// fixed 112×112 circle, white background, 24px padding around the 64px icon, with a
// soft drop shadow. Size is locked explicitly (not derived from the child icon size)
// so the badge stays 112×112 regardless of the icon's fontSize. Figma specifies a
// symmetric ~20px glow; the theme has no matching custom shadow, so we use the closest
// MUI default elevation (shadows[6]).
export const EmptyChatIconBadge = styled(Box, { label: 'EmptyChatIconBadge' })(({ theme }) => ({
  boxSizing: 'border-box',
  width: 112,
  height: 112,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3),
  borderRadius: '50%',
  backgroundColor: theme.palette.common.white,
  boxShadow: theme.shadows[6],
}));

export const EmptyPanelContent = styled(Box, { label: 'EmptyPanelContent' })(({ theme }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
  // Off-white (#FBFDFF) — matches the open-conversation right-side background
  // (.sendbird-conversation in styles.css, via --dokka-chat-surface). The header
  // (EmptyPanelHeader) stays white; everything below it on the right side uses this
  // tint. Single source of truth: theme.palette.chat.surface, with a literal fallback
  // for themes that don't define palette.chat (e.g. the bootstrap theme in
  // @/initializers/src/theme) so this never crashes.
  backgroundColor: theme.palette.chat?.surface ?? 'rgba(251, 253, 255, 1)',
}));
